/**
 * Copyright (c) 2024, Ouster, Inc.
 * All rights reserved.
 */
import {
  action,
  thunk,
  Action as EzPzAction,
  Thunk as EzPzThunk,
} from 'easy-peasy';

export type State = {
  isOpen: boolean;
  isLoading: boolean;
  onConfirm: () => Promise<void>;
  title?: string;
  body?: string;
  disclaimer?: string;
  bullets?: string[];
  details?: { label: string; value: string }[];
  confirmLabel?: string;
  confirmVariant?: 'solid' | 'outline' | 'ghost' | 'link';
  cancelLabel?: string;
  isDestructive?: boolean;
};

export const defaultState: State = {
  isOpen: false,
  isLoading: false,
  onConfirm: async () => {},
  title: undefined,
  body: undefined,
  disclaimer: undefined,
  bullets: undefined,
  details: undefined,
  confirmLabel: undefined,
  confirmVariant: undefined,
  cancelLabel: undefined,
  isDestructive: undefined,
};

export interface ConfirmDialogModel extends State {
  openConfirmDialog: EzPzThunk<
    ConfirmDialogModel,
    {
      onConfirm: () => Promise<void>;
      title?: string;
      body?: string;
      disclaimer?: string;
      bullets?: string[];
      details?: { label: string; value: string }[];
      confirmLabel?: string;
      confirmVariant?: 'solid' | 'outline' | 'ghost' | 'link';
      cancelLabel?: string;
      isDestructive?: boolean;
    }
  >;
  closeConfirmDialog: EzPzAction<ConfirmDialogModel>;
  setIsOpen: EzPzAction<ConfirmDialogModel, boolean>;
  setIsLoading: EzPzAction<ConfirmDialogModel, boolean>;
  setOnConfirm: EzPzAction<ConfirmDialogModel, () => Promise<void>>;
  setTitle: EzPzAction<ConfirmDialogModel, string | undefined>;
  setBody: EzPzAction<ConfirmDialogModel, string | undefined>;
  setDisclaimer: EzPzAction<ConfirmDialogModel, string | undefined>;
  setBullets: EzPzAction<ConfirmDialogModel, string[] | undefined>;
  setDetails: EzPzAction<
    ConfirmDialogModel,
    { label: string; value: string }[] | undefined
  >;
  setConfirmLabel: EzPzAction<ConfirmDialogModel, string | undefined>;
  setConfirmVariant: EzPzAction<
    ConfirmDialogModel,
    'solid' | 'outline' | 'ghost' | 'link' | undefined
  >;
  setCancelLabel: EzPzAction<ConfirmDialogModel, string | undefined>;
  setIsDestructive: EzPzAction<ConfirmDialogModel, boolean | undefined>;
}

export const modal: ConfirmDialogModel = {
  ...defaultState,
  openConfirmDialog: thunk((actions, payload) => {
    actions.setOnConfirm(payload.onConfirm);
    actions.setTitle(payload.title);
    actions.setBody(payload.body);
    actions.setDisclaimer(payload.disclaimer);
    actions.setConfirmLabel(payload.confirmLabel);
    actions.setConfirmVariant(payload.confirmVariant);
    actions.setCancelLabel(payload.cancelLabel);
    actions.setIsDestructive(payload.isDestructive);
    actions.setBullets(payload.bullets);
    actions.setDetails(payload.details);
    actions.setIsOpen(true);
  }),
  closeConfirmDialog: action((state) => {
    state.isOpen = false;
    state.isLoading = false;
    state.title = undefined;
    state.body = undefined;
    state.disclaimer = undefined;
    state.bullets = undefined;
    state.details = undefined;
    state.confirmLabel = undefined;
    state.confirmVariant = undefined;
    state.cancelLabel = undefined;
    state.isDestructive = undefined;
  }),
  setIsOpen: action((state, isOpen) => {
    state.isOpen = isOpen;
  }),
  setTitle: action((state, title) => {
    state.title = title;
  }),
  setBody: action((state, body) => {
    state.body = body;
  }),
  setDisclaimer: action((state, disclaimer) => {
    state.disclaimer = disclaimer;
  }),
  setBullets: action((state, bullets) => {
    state.bullets = bullets;
  }),
  setDetails: action((state, details) => {
    state.details = details;
  }),
  setConfirmLabel: action((state, confirmLabel) => {
    state.confirmLabel = confirmLabel;
  }),
  setConfirmVariant: action((state, confirmVariant) => {
    state.confirmVariant = confirmVariant;
  }),
  setCancelLabel: action((state, cancelLabel) => {
    state.cancelLabel = cancelLabel;
  }),
  setIsDestructive: action((state, isDestructive) => {
    state.isDestructive = isDestructive;
  }),
  setIsLoading: action((state, isLoading) => {
    state.isLoading = isLoading;
  }),
  setOnConfirm: action((state, onConfirm) => {
    state.onConfirm = onConfirm;
  }),
};
